import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ChatMessage } from '../utils/api';

interface WebSocketMessage {
  type: 'new_message' | 'joined_thread' | 'left_thread' | 'new_notification';
  message?: ChatMessage;
  thread_id?: string;
  notification?: any;
}

interface UseWebSocketReturn {
  isConnected: boolean;
  joinThread: (threadId: string) => void;
  leaveThread: (threadId: string) => void;
  onNewMessage: (callback: (message: ChatMessage) => void) => void;
  onNewNotification: (callback: (notification: any) => void) => void;
  disconnect: () => void;
  getWebSocket: () => WebSocket | null;
}

// Determine WebSocket URL from API URL or use default
// This matches the logic in api.ts to ensure consistency
const getWebSocketUrl = (): string => {
  // First check if explicit WebSocket URL is set
  const wsUrl = process.env.REACT_APP_WS_URL;
  if (wsUrl) {
    return wsUrl;
  }
  
  // Derive from API URL (same logic as api.ts)
  let apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  
  // Ensure the URL is absolute (same logic as api.ts)
  if (apiUrl && !apiUrl.startsWith('http://') && !apiUrl.startsWith('https://')) {
    // If it's missing the protocol, add https://
    apiUrl = `https://${apiUrl}`;
  }
  
  // Remove trailing slash if present
  apiUrl = apiUrl.replace(/\/$/, '');
  
  // Convert to WebSocket URL
  if (apiUrl.startsWith('http://')) {
    return apiUrl.replace('http://', 'ws://');
  } else if (apiUrl.startsWith('https://')) {
    return apiUrl.replace('https://', 'wss://');
  }
  
  // Default fallback (shouldn't reach here, but just in case)
  return 'ws://localhost:8000';
};

const WS_BASE_URL = getWebSocketUrl();

export const useWebSocket = (): UseWebSocketReturn => {
  const { user, isAuthenticated } = useAuth();
  const wsRef = useRef<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const messageCallbackRef = useRef<((message: ChatMessage) => void) | null>(null);
  const notificationCallbackRef = useRef<((notification: any) => void) | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  const connect = useCallback(() => {
    if (!user || !isAuthenticated) return;

    try {
      const wsUrl = `${WS_BASE_URL}/ws/${user.id}`;
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        setIsConnected(true);
        reconnectAttempts.current = 0;
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data: WebSocketMessage = JSON.parse(event.data);

          if (data.type === 'new_message' && data.message) {
            if (messageCallbackRef.current) {
              messageCallbackRef.current(data.message);
            } else {
              console.warn('Received new_message but callback is not set!', data.message);
            }
          } else if (data.type === 'new_notification' && data.notification) {
            if (notificationCallbackRef.current) {
              notificationCallbackRef.current(data.notification);
            } else {
              console.warn('Received new_notification but callback is not set!', data.notification);
            }
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      // Expose WebSocket for notification context
      (window as any).__notificationWebSocket = wsRef.current;

      wsRef.current.onclose = (event) => {
        setIsConnected(false);

        // Attempt to reconnect if not a manual disconnect
        if (event.code !== 1000 && reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.pow(2, reconnectAttempts.current) * 1000; // Exponential backoff
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current++;
            connect();
          }, delay);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
    }
  }, [user, isAuthenticated]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close(1000, 'Manual disconnect');
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const joinThread = useCallback((threadId: string) => {
    if (wsRef.current && isConnected && wsRef.current.readyState === WebSocket.OPEN) {
      const message = {
        type: 'join_thread',
        thread_id: threadId
      };
      wsRef.current.send(JSON.stringify(message));
    } else {
      console.warn('⚠️ Cannot join thread - WebSocket not connected:', {
        hasWebSocket: !!wsRef.current,
        isConnected,
        readyState: wsRef.current?.readyState
      });
    }
  }, [isConnected]);

  const leaveThread = useCallback((threadId: string) => {
    if (wsRef.current && isConnected) {
      const message = {
        type: 'leave_thread',
        thread_id: threadId
      };
      wsRef.current.send(JSON.stringify(message));
    }
  }, [isConnected]);

  const onNewMessage = useCallback((callback: (message: ChatMessage) => void) => {
    messageCallbackRef.current = callback;
  }, []);

  const onNewNotification = useCallback((callback: (notification: any) => void) => {
    notificationCallbackRef.current = callback;
  }, []);

  const getWebSocket = useCallback(() => {
    return wsRef.current;
  }, []);

  // Connect when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [isAuthenticated, user, connect, disconnect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    isConnected,
    joinThread,
    leaveThread,
    onNewMessage,
    onNewNotification,
    disconnect,
    getWebSocket
  };
};
